/**
 * Do something when a server-side counter goes up.
 *
 * Three overlays wanted this - a card in flight, a speech bubble, the trick
 * just won - and all three hand-rolled it, and all three got the same thing
 * wrong. Writing it once is the point.
 *
 * ## The rule that was broken
 *
 * The effect must depend on **the counter alone**. Everything else has to be
 * reached through a ref.
 *
 * The values these overlays need - the parsed trick, the `says` map, a
 * `useCallback` whose own dependency is a fresh array - are new objects on
 * every render. Name one in a dependency array and the effect re-runs on every
 * render; the re-run fires the previous cleanup, which cancels the timer that
 * was going to hide the thing; and the re-run itself returns early, because the
 * counter has not moved, so no new timer is ever set.
 *
 * The overlay then stays up until the next event replaces it. That is not a
 * subtle failure - the trick winner banner simply never went away - and it
 * survived review three times because each copy looked correct in isolation.
 *
 * ## The other two rules
 *
 * **The first value seen is a baseline, not an event.** Somebody opening a
 * table mid-round must not be shown a trick that finished ten minutes ago.
 *
 * **A decrease is a baseline too.** These counters are per round, so a new deal
 * resets them, and counting backwards is not something that just happened.
 */

import { useLayoutEffect, useRef } from 'react';

/**
 * The decision, separated from the React that drives it.
 *
 * Pure so it can be tested without a DOM — the same split `countdown.ts` uses,
 * and for a better reason here: the three rules below are the whole value of
 * this file, and the bug they encode got past review three times. The React
 * wiring is four lines; the rules deserved a test and could not have one while
 * they lived inside an effect.
 *
 * `seen` is what was last accepted, or null before anything has been. Returns
 * whether to fire and what to remember.
 */
export function counterStep(
  seen: number | null,
  counter: number,
): { fire: boolean; next: number } {
  // The first value seen is a baseline, not an event: somebody opening a table
  // mid-round must not be shown a trick that finished ten minutes ago.
  if (seen === null) return { fire: false, next: counter };

  // A decrease is a baseline too. These counters are per round, so a new deal
  // resets them, and counting backwards is not something that just happened.
  if (counter < seen) return { fire: false, next: counter };

  // Unchanged is not an event either, which is what makes it safe for the
  // effect to re-run.
  if (counter === seen) return { fire: false, next: seen };

  return { fire: true, next: counter };
}

export function useCounterEvent(counter: number, onIncrease: () => void): void {
  const seen = useRef<number | null>(null);

  // The callback closes over this render's values; keeping it in a ref is what
  // lets the effect below name only the counter.
  const fire = useRef(onIncrease);
  fire.current = onIncrease;

  /*
   * Before the browser paints, not after.
   *
   * Everything driven by these counters is a reaction to a packet that has
   * ALREADY updated the table - the card is on the discard, the card is in
   * your hand - and the reaction's job is usually to hold part of that back
   * until an animation has run. A plain effect runs after the paint, so the
   * new state got one frame on screen before being hidden again: a played
   * card blinked onto the pile and then vanished for the length of its
   * flight. Measured at 5ms, which is a frame, which is enough to see.
   */
  useLayoutEffect(() => {
    const step = counterStep(seen.current, counter);
    seen.current = step.next;
    if (step.fire) fire.current();
  }, [counter]);
}
